import { localize } from "@/i18n/translate";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, X } from "lucide-react";
import { api, errorCode } from "@/lib/api";
import { usePortal } from "../i18n";
import { TicketRow } from "./ClientDashboard";
import { ErrorNote, PageTitle, Spinner } from "../ui";
const Stat = ({
  label,
  value,
  to,
  testId,
  accent
}) => <Link to={to} data-testid={testId} className="group border border-ink/15 bg-white/60 p-5 transition-colors hover:border-ink">
    <p className="eyebrow">{localize(label)}</p>
    <p className={`mt-4 font-display text-4xl font-extrabold tracking-[-0.03em] ${accent && value ? "text-signal" : ""}`}>{localize(value ?? "–")}</p>
  </Link>;
export const ApproveButtons = ({
  user,
  onDone
}) => {
  const {
    p,
    errText
  } = usePortal();
  const [err, setErr] = useState(null);
  const set = async status => {
    setErr(null);
    try {
      await api.patch(`/admin/users/${user.id}`, {
        status
      });
      onDone?.();
    } catch (e) {
      setErr(errText(errorCode(e)));
    }
  };
  return <div>
      <div className="flex flex-wrap gap-2">
        {localize(user.status !== "approved" && <button type="button" onClick={() => set("approved")} className="inline-flex items-center gap-1.5 bg-ink px-3 py-2 text-sm font-medium text-paper hover:bg-signal" data-testid={`approve-${user.email}`}>
            <Check className="h-4 w-4" aria-hidden="true" /> {localize(p.adm.approve)}
          </button>)}
        {localize(user.status !== "rejected" && <button type="button" onClick={() => set("rejected")} className="inline-flex items-center gap-1.5 border border-ink/30 px-3 py-2 text-sm font-medium hover:border-signal hover:text-signal" data-testid={`reject-${user.email}`}>
            <X className="h-4 w-4" aria-hidden="true" /> {localize(p.adm.reject)}
          </button>)}
      </div>
      {localize(err && <p className="mt-2 text-xs text-signaldark">{localize(err)}</p>)}
    </div>;
};
export default function AdminDashboard() {
  const {
    p,
    errText
  } = usePortal();
  const [stats, setStats] = useState(null);
  const [tickets, setTickets] = useState(null);
  const [err, setErr] = useState(null);
  const load = useCallback(() => {
    Promise.all([api.get("/admin/stats"), api.get("/admin/tickets?status=offen")]).then(([s, t]) => {
      setStats(s.data ?? {});
      setTickets(Array.isArray(t.data) ? t.data.slice(0, 8) : []);
    }).catch(e => setErr(errText(errorCode(e))));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  return <div data-testid="admin-dashboard">
      <PageTitle eyebrow={p.admin} title={localize(p.adm.overview)}><Link className="inline-flex bg-signal px-5 py-3 text-sm font-semibold text-white" to="/admin/workspace">{p.nav.clients} · Workspace →</Link></PageTitle>
      <ErrorNote>{localize(err)}</ErrorNote>
      <div className="mt-10 grid grid-cols-2 gap-4 lg:grid-cols-3">
        <Stat label={p.adm.unread} value={stats?.unread_tickets} to="/admin/anfragen" testId="stat-unread" accent />
        <Stat label={p.adm.open} value={stats?.open_tickets} to="/admin/anfragen" testId="stat-open" />
        <Stat label={p.adm.clients} value={stats?.clients} to="/admin/kunden?status=approved" testId="stat-clients" />
      </div>
      <section className="mt-14" aria-labelledby="latest-h">
        <h2 id="latest-h" className="font-display text-2xl font-bold tracking-[-0.02em]">{localize(p.adm.latest)}</h2>
        {localize(tickets === null ? <Spinner label={p.loading} /> : tickets.length === 0 ? <p className="mt-4 text-mute">{localize(p.adm.noTickets)}</p> : <ul className="mt-4 border-t border-ink/15">
            {localize(tickets.map(t => <TicketRow key={t.id} t={t} to={`/admin/anfragen/${t.id}`} showClient />))}
          </ul>)}
      </section>
    </div>;
}
